/**
 * useLabelDensity — density-adaptive label visibility for deck.gl TextLayer
 * consumers. One source of truth for the "Label spacing" behaviour: the route
 * manager's DeckMarkerRenderer, the container view's WebGLSequenceMarkerLayer,
 * the routing-tab edit map, and the bulk-select dialog all share it.
 *
 * Algorithm (mirrors the original in DeckMarkerRenderer):
 *   1. Convert viewport bounds → world pixels at the current zoom.
 *   2. Compute how many `LABEL_RADIUS_BASE_PX`-radius circles fit in that area.
 *   3. Scale the collision radius up when stop count exceeds that cap so the
 *      number of visible labels stays roughly proportional to viewport area
 *      (no "label avalanche" at high zoom).
 *   4. Multiply by the user's "Label spacing" slider (level 1–6 → 0.25–8×).
 *   5. Walk items from last to first (so higher-priority items, sorted last,
 *      win their bucket) and accept each whose label doesn't overlap any
 *      already-accepted neighbour within the spatial hash's 3×3 grid window.
 *
 * Levels (matches the user-facing "Label spacing" slider — higher number = more spacing = fewer labels):
 *   0     → no spacing: show every label (collision off; the default)
 *   1..6  → density-adaptive collision, radius grows with the level
 *           (1 = tightest spacing / most labels, 6 = widest spacing / fewest labels)
 */
import { useMemo } from 'react'

import { collideByRadius } from './collideByRadius'
import { toWorldPixel } from './webMercator'

const LABEL_RADIUS_BASE_PX = 44
// Indexed by (level - 1) for levels 1–6. Higher slider level → larger multiplier → more spacing → fewer labels.
const LABEL_SPACING_MULTIPLIERS = [0.25, 0.5, 1, 2, 4, 8]
// Past this count show-all is an unreadable mass of overlapping text costing a
// TextLayer quad per character, so level 0 degrades to the tightest spacing.
export const MAX_UNCOLLIDED_LABELS = 750

const defaultGetId = d => d?.id
const defaultGetPosition = d => d?.position

/**
 * @param {Object} params
 * @param {Array} params.items        Items in render order (last = highest priority).
 * @param {Array<number>} [params.bounds]  Viewport bounds [west, south, east, north].
 *   Required for collision; without it the hook falls back to "show all".
 * @param {number} [params.zoom]      Current map zoom level.
 * @param {number} [params.level=0]   0–6 (see module doc).
 * @param {number} [params.textOffsetY=0]  Pixel offset from item position to the
 *   label's vertical centre (pills place the text above the anchor, so the
 *   route manager passes `PILL_TEXT_PIXEL_OFFSET_Y`).
 * @param {(item: any) => any}        [params.getId]
 * @param {(item: any) => [number, number]} [params.getPosition]
 * @returns {Set} ids of items whose labels should render.
 */
export function useLabelDensity({
  items,
  bounds,
  zoom,
  level = 0,
  textOffsetY = 0,
  getId = defaultGetId,
  getPosition = defaultGetPosition,
}) {
  return useMemo(() => {
    if (!items || items.length === 0) return new Set()
    // level 0 = no spacing → show every label, until the count makes that meaningless.
    const effectiveLevel = level > 0 || items.length > MAX_UNCOLLIDED_LABELS ? Math.max(level, 1) : 0
    if (effectiveLevel <= 0) return new Set(items.map(getId))
    if (!bounds || !Number.isFinite(zoom)) return new Set(items.map(getId))

    const [west, south, east, north] = bounds
    const [vpX0, vpY0] = toWorldPixel(west, south, zoom)
    const [vpX1, vpY1] = toWorldPixel(east, north, zoom)
    const vpW = Math.abs(vpX1 - vpX0)
    const vpH = Math.abs(vpY1 - vpY0)
    const vpArea = vpW * vpH

    const maxNonOverlapping = Math.max(1, vpArea / (LABEL_RADIUS_BASE_PX * LABEL_RADIUS_BASE_PX))
    const densityFactor = Math.max(1, Math.sqrt(items.length / maxNonOverlapping))
    const userMultiplier = LABEL_SPACING_MULTIPLIERS[Math.max(0, Math.min(LABEL_SPACING_MULTIPLIERS.length - 1, effectiveLevel - 1))]

    return collideByRadius({
      items,
      zoom,
      radiusPx: LABEL_RADIUS_BASE_PX * densityFactor * userMultiplier,
      offsetY: textOffsetY,
      getId,
      getPosition,
    })
  }, [items, bounds, zoom, level, textOffsetY, getId, getPosition])
}
